<template>
  <div class="about-container">
    <!-- 顶部横幅 -->
    <div class="hero-section" ref="heroSection">
      <div class="hero-content" :style="{ transform: `translateY(${scrollY * 0.5}px)` }">
        <h1 class="animate__animated animate__fadeInUp">关于我们</h1>
        <p class="animate__animated animate__fadeInUp animate__delay-1s">
          创新引领未来，科技服务社会
        </p>
      </div>
      <div class="hero-overlay"></div>
    </div>

    <!-- 公司简介 -->
    <div class="company-intro">
      <div class="intro-content">
        <h2>公司简介</h2>
        <div class="intro-grid">
          <div class="intro-text">
            <p v-for="(para, index) in introParagraphs" :key="index">{{ para }}</p>
          </div>
          <div class="intro-features">
            <div class="feature-card">
              <el-icon class="feature-icon"><Monitor /></el-icon>
              <h3>技术服务创新</h3>
              <p>持续深耕应用技术，优化 X 射线光学项目落地解决方案</p>
            </div>
            <div class="feature-card">
              <el-icon class="feature-icon"><SetUp /></el-icon>
              <h3>品质保障</h3>
              <p>严格的选型评估与到货验收流程，保障代理产品稳定可靠</p>
            </div>
            <div class="feature-card">
              <el-icon class="feature-icon"><Service /></el-icon>
              <h3>专业服务</h3>
              <p>提供全方位的技术支持和定制化解决方案</p>
            </div>
            <div class="feature-card">
              <el-icon class="feature-icon"><Connection /></el-icon>
              <h3>合作共赢</h3>
              <p>与客户建立长期稳定的合作伙伴关系</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 发展历程 -->
    <div class="history-section">
      <div class="history-content">
        <h2>发展历程</h2>
        <div class="timeline-horizontal">
          <div class="timeline-track">
            <div class="timeline-item" v-for="(item, index) in historyItems" :key="index">
              <div class="year">{{ item.year }}</div>
              <div class="dot"></div>
              <div class="event">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { Monitor, SetUp, Service, Connection } from '@element-plus/icons-vue'
import { fetchContentByCode, parseJsonField } from '@/utils/cms'

const INTRO_CACHE_KEY = 'xingxu_about_intro'
const HISTORY_CACHE_KEY = 'xingxu_about_history'

const defaultIntroParagraphs = [
  '北京行序科技有限公司专注 X 射线光学领域产品引进与技术服务，面向科研院所、高等院校及工业领域，提供高性能 X 射线光学元件及整套系统解决方案。',
  '公司聚焦 X 射线光学应用场景，在多毛细管聚焦、准直光学系统、X 射线导光板等方向拥有丰富的项目选型与应用实践经验。所代理的各类产品，已广泛服务于材料科学、生物医学、工业无损检测等行业。',
  '秉持 “创新驱动发展，品质创造价值” 的理念，我们持续优化应用方案。依托与国内外上游原厂、顶尖科研机构的深度合作，为客户筛选匹配适配产品，输出专业的技术支持与项目配套服务。',
  '公司产品线覆盖 X 射线源、X 射线光学元件、X 射线 / 可见光探测器、X 射线光谱仪及无损检测相关设备，可结合客户工况提供定制化成套应用方案。'
]

const defaultHistoryItems = [
  { year: '2021', title: '公司成立', description: '北京行序科技有限公司正式成立，开启X射线光学技术创新之路' },
  { year: '2022', title: '技术积累', description: '完成核心技术研发，获得多项专利授权' },
  { year: '2023', title: '技术突破', description: '成功研发新一代X射线聚焦光学系统，实现关键技术突破' }
]

function splitIntro(content) {
  if (!content) return []
  const text = String(content)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
  return text.split(/\n+/).map(item => item.trim()).filter(Boolean)
}

function sameParagraphs(left, right) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function readCache(key) {
  try {
    return sessionStorage.getItem(key) || ''
  } catch (e) {
    return ''
  }
}

function writeCache(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch (e) {
    // ignore quota / private mode
  }
}

export default {
  name: 'About',
  components: {
    Monitor,
    SetUp,
    Service,
    Connection
  },
  setup() {
    const scrollY = ref(0)
    const cachedIntro = splitIntro(readCache(INTRO_CACHE_KEY))
    const cachedHistory = parseJsonField(readCache(HISTORY_CACHE_KEY), null)
    const introParagraphs = ref(cachedIntro.length ? cachedIntro : defaultIntroParagraphs)
    const historyItems = ref(Array.isArray(cachedHistory) && cachedHistory.length ? cachedHistory : defaultHistoryItems)

    const handleScroll = () => {
      scrollY.value = window.scrollY
    }

    const loadContents = async () => {
      const [intro, history] = await Promise.all([
        fetchContentByCode('about_intro'),
        fetchContentByCode('about_history')
      ])

      if (intro?.content) {
        const paragraphs = splitIntro(intro.content)
        if (paragraphs.length && !sameParagraphs(introParagraphs.value, paragraphs)) {
          introParagraphs.value = paragraphs
        }
        if (intro.content) writeCache(INTRO_CACHE_KEY, intro.content)
      }

      if (history?.content) {
        const parsed = parseJsonField(history.content, null)
        if (Array.isArray(parsed) && parsed.length && JSON.stringify(parsed) !== JSON.stringify(historyItems.value)) {
          historyItems.value = parsed
        }
        writeCache(HISTORY_CACHE_KEY, typeof history.content === 'string' ? history.content : JSON.stringify(parsed))
      }
    }

    loadContents()

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      scrollY,
      introParagraphs,
      historyItems
    }
  }
}
</script>

<style scoped>
.about-container {
  width: 100%;
}

/* 顶部横幅 */
.hero-section {
  height: 400px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  text-align: center;
  color: white;
  z-index: 2;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 48px;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 2px;
}

.hero-content p {
  font-size: 24px;
  opacity: 0.9;
  letter-spacing: 1px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

/* 公司简介 */
.company-intro {
  padding: 80px 0;
  background: white;
}

.intro-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.intro-content h2 {
  font-size: 36px;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 60px;
  font-weight: 600;
}

.intro-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
}

.intro-text {
  color: #4a5568;
  font-size: 16px;
  line-height: 1.8;
}

.intro-text p {
  margin-bottom: 20px;
}

.intro-text p:last-child {
  margin-bottom: 0;
}

.intro-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.feature-card {
  background: #f8fafc;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  font-size: 36px;
  color: #1890ff;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 10px;
  font-weight: 600;
}

.feature-card p {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

/* 发展历程 - 横向样式 */
.history-section {
  padding: 80px 0;
  background: #f8fafc;
  overflow: hidden;
}

.history-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.history-content h2 {
  font-size: 36px;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 60px;
  font-weight: 600;
}

.timeline-horizontal {
  position: relative;
  padding: 40px 0;
}

.timeline-track {
  display: flex;
  justify-content: space-between;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
}

.timeline-track::before {
  content: '';
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e2e8f0;
  z-index: 1;
}

.timeline-item {
  position: relative;
  width: 300px;
  text-align: center;
  z-index: 2;
}

.year {
  background: #1890ff;
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 20px;
}

.dot {
  width: 16px;
  height: 16px;
  background: #1890ff;
  border-radius: 50%;
  margin: 0 auto 20px;
  position: relative;
  z-index: 3;
}

.dot::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid #1890ff;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.event {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.event:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.event h3 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 8px;
  font-weight: 600;
}

.event p {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  /* 顶部横幅调整 */
  .hero-section {
    height: 300px;
  }

  .hero-content h1 {
    font-size: 32px;
  }

  .hero-content p {
    font-size: 18px;
  }

  /* 公司简介部分调整 */
  .company-intro {
    padding: 40px 0;
  }

  .intro-content {
    padding: 0 20px;
  }

  .intro-content h2 {
    font-size: 28px;
    margin-bottom: 30px;
  }

  .intro-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .intro-text {
    font-size: 15px;
  }

  .intro-features {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .feature-card {
    padding: 20px;
  }

  .feature-icon {
    font-size: 28px;
    margin-bottom: 15px;
  }

  .feature-card h3 {
    font-size: 18px;
  }

  .feature-card p {
    font-size: 14px;
  }

  /* 发展历程部分调整 */
  .history-section {
    padding: 40px 0;
  }

  .history-content {
    padding: 0 20px;
  }

  .history-content h2 {
    font-size: 28px;
    margin-bottom: 30px;
  }

  .timeline-track {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }

  .timeline-track::before {
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    height: 100%;
    transform: translateX(-50%);
  }

  .timeline-item {
    width: 100%;
    max-width: 300px;
  }

  .event {
    margin-top: 20px;
    padding: 15px;
  }

  .event h3 {
    font-size: 16px;
  }

  .event p {
    font-size: 14px;
  }
}

/* 小屏幕手机适配 */
@media (max-width: 480px) {
  .hero-section {
    height: 250px;
  }

  .hero-content h1 {
    font-size: 28px;
  }

  .hero-content p {
    font-size: 16px;
  }

  .intro-content h2 {
    font-size: 24px;
  }

  .feature-card {
    padding: 15px;
  }

  .timeline-item {
    max-width: 250px;
  }
}
</style> 